import { CalendarCompletedMark, CalendarPlannedMark } from './CalendarCompletedMark';
import { isPastCalendarDay } from '../utils/isPastCalendarDay';
import { displayDigits } from '../utils/displayDigits';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CalendarGridItem } from '../hooks/useCalendarData';
import type { CalendarWorkout } from '../services/api';
import { addJalaliDays, type JalaliDayRef } from '../utils/jalali';
import {
  getWeekStripDays,
  isJalaliPickerLocale,
  weekStripDisplayDay,
} from '../utils/calendarPickerLocale';
import { useLocale } from '../hooks/useLocale';
import { getDayWorkouts } from '../features/calendar/calendarHelpers';

const SWIPE_RATIO = 0.16;
const SNAP_MS = 260;
/** Ignore micro-movement before treating gesture as horizontal swipe. */
const DRAG_LOCK_PX = 10;

export type WeekSlideDirection = 'prev' | 'next';

export interface NmWeekStripProps {
  weekAnchor: { year: number; month: number; day: number };
  selectedDateKey: string;
  dayByKey: Map<string, CalendarGridItem>;
  onSelect: (dateKey: string) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onWeekWillChange?: (direction: WeekSlideDirection) => void;
}

/** VO2 DS week strip — swipe carousel (no arrow nav). */
export function NmWeekStrip({
  weekAnchor,
  selectedDateKey,
  dayByKey,
  onSelect,
  onPrevWeek,
  onNextWeek,
  onWeekWillChange,
}: NmWeekStripProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const useJalaliStrip = isJalaliPickerLocale(locale);

  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [animating, setAnimating] = useState(false);
  const dragStart = useRef<{ x: number; y: number; t: number } | null>(null);
  const didDrag = useRef(false);
  const lockAxis = useRef<'x' | 'y' | null>(null);
  const capturedPointerId = useRef<number | null>(null);

  const adjacentAnchors = useMemo(() => {
    const { year, month, day } = weekAnchor;
    const offsets = useJalaliStrip ? [7, 0, -7] : [-7, 0, 7];
    return offsets.map((offset) => {
      const j = addJalaliDays(year, month, day, offset);
      return { year: j.year, month: j.month, day: j.dat };
    });
  }, [weekAnchor, useJalaliStrip]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => setViewportWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const baseOffset = viewportWidth > 0 ? -viewportWidth : 0;
  const trackOffset = baseOffset + dragX;

  const finishSwipe = useCallback(
    (direction: 'prev' | 'next' | 'snap') => {
      if (viewportWidth <= 0) return;

      if (direction === 'snap') {
        setAnimating(true);
        setDragX(0);
        window.setTimeout(() => setAnimating(false), SNAP_MS);
        return;
      }

      setAnimating(true);
      const nextExit = useJalaliStrip ? viewportWidth : -viewportWidth;
      const exit = direction === 'next' ? nextExit : -nextExit;
      setDragX(exit);

      if (direction === 'next') onWeekWillChange?.('next');
      else onWeekWillChange?.('prev');

      window.setTimeout(() => {
        if (direction === 'next') onNextWeek();
        else onPrevWeek();
        setAnimating(false);
        requestAnimationFrame(() => setDragX(0));
      }, SNAP_MS);
    },
    [onNextWeek, onPrevWeek, onWeekWillChange, useJalaliStrip, viewportWidth],
  );

  const resolveSwipe = useCallback(
    (deltaX: number, velocityX: number) => {
      const threshold = viewportWidth * SWIPE_RATIO;
      const flick = Math.abs(velocityX) > 0.45 && Math.abs(deltaX) > 18;

      const logicalDeltaX = useJalaliStrip ? -deltaX : deltaX;
      const logicalVelocityX = useJalaliStrip ? -velocityX : velocityX;

      if (logicalDeltaX < -threshold || (flick && logicalVelocityX < 0)) finishSwipe('next');
      else if (logicalDeltaX > threshold || (flick && logicalVelocityX > 0)) finishSwipe('prev');
      else finishSwipe('snap');
    },
    [finishSwipe, useJalaliStrip, viewportWidth],
  );

  const releaseCapture = useCallback((el: HTMLElement) => {
    if (capturedPointerId.current == null) return;
    try {
      el.releasePointerCapture(capturedPointerId.current);
    } catch {
      /* pointer already released */
    }
    capturedPointerId.current = null;
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (animating) return;
    didDrag.current = false;
    lockAxis.current = null;
    dragStart.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  }, [animating]);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragStart.current || animating) return;

      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;

      if (!lockAxis.current) {
        if (Math.abs(dx) < DRAG_LOCK_PX && Math.abs(dy) < DRAG_LOCK_PX) return;
        lockAxis.current = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
        if (lockAxis.current === 'y') {
          dragStart.current = null;
          return;
        }
      }

      if (lockAxis.current !== 'x') return;

      if (capturedPointerId.current !== e.pointerId) {
        capturedPointerId.current = e.pointerId;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      }

      didDrag.current = true;
      e.preventDefault();

      const resistance = 0.35;
      const clamped =
        dx < 0
          ? Math.max(dx, -viewportWidth * 0.92)
          : Math.min(dx, viewportWidth * 0.92);
      setDragX(clamped * (Math.abs(clamped) > viewportWidth * 0.55 ? resistance : 1));
    },
    [animating, viewportWidth],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const el = e.currentTarget as HTMLElement;
      releaseCapture(el);

      if (!dragStart.current) return;

      const dx = e.clientX - dragStart.current.x;
      const dt = Math.max(Date.now() - dragStart.current.t, 1);
      const velocityX = dx / dt;
      const wasDrag = didDrag.current;
      dragStart.current = null;
      lockAxis.current = null;
      didDrag.current = false;

      if (wasDrag) {
        resolveSwipe(dx, velocityX);
      }
    },
    [releaseCapture, resolveSwipe],
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent) => {
      releaseCapture(e.currentTarget as HTMLElement);
      dragStart.current = null;
      lockAxis.current = null;
      didDrag.current = false;
      finishSwipe('snap');
    },
    [finishSwipe, releaseCapture],
  );

  return (
    <div className="cal-week-panel" dir="rtl">
      <div
        ref={viewportRef}
        className="weekstrip-carousel"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        aria-roledescription="carousel"
        aria-label={t('calendar.weekStrip', 'Week selector')}
      >
        <div
          className={`weekstrip-track${animating ? ' weekstrip-track--anim' : ''}`}
          style={{
            transform: viewportWidth > 0 ? `translate3d(${trackOffset}px, 0, 0)` : undefined,
          }}
        >
          {adjacentAnchors.map((anchor, slideIdx) => {
            const days = getWeekStripDays(anchor.year, anchor.month, anchor.day, locale);
            return (
              <div key={`${anchor.year}-${anchor.month}-${anchor.day}-${slideIdx}`} className="weekstrip-slide">
                <div
                  className={`weekstrip${useJalaliStrip ? ' weekstrip--jalali' : ''}`}
                  dir={useJalaliStrip ? 'rtl' : 'ltr'}
                >
                  {days.map((d) => (
                    <WeekDayButton
                      key={d.dateKey}
                      day={d}
                      locale={locale}
                      item={dayByKey.get(d.dateKey)}
                      selected={d.dateKey === selectedDateKey}
                      onSelect={() => onSelect(d.dateKey)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function WeekDayButton({
  day,
  locale,
  item,
  selected,
  onSelect,
}: {
  day: JalaliDayRef;
  locale: string;
  item?: CalendarGridItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const workouts = getDayWorkouts(item) as CalendarWorkout[];
  const hasPlanned = workouts.some(
    (w) => !(Number(w.total_time) || Number(w.tss_actual)),
  );
  const hasDone = workouts.some(
    (w) => !!(Number(w.total_time) || Number(w.tss_actual)),
  );
  const withWorkout = hasPlanned || hasDone;
  const isPast = isPastCalendarDay(day.dateKey);

  const cls = [
    'weekstrip__day',
    selected ? 'weekstrip__day--selected' : '',
    withWorkout ? 'weekstrip__day--workout' : '',
    day.isToday ? 'weekstrip__day--today' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={cls} data-date-key={day.dateKey} aria-pressed={selected} aria-current={day.isToday ? 'date' : undefined} onClick={onSelect}>
      <span className="weekstrip__label">
        <span className="weekstrip__dow">{day.dowLabel}</span>
        <span className="weekstrip__num">{displayDigits(weekStripDisplayDay(day, locale))}</span>
      </span>
      <span className="weekstrip__indicator" aria-hidden>
        {hasDone && <CalendarCompletedMark />}
        {hasPlanned && <CalendarPlannedMark missed={isPast} />}
      </span>
    </button>
  );
}
